import type {
  BaseServiceOptions,
  Pagination,
  THistoryActions,
  TRecordAction,
} from '@/core/types'
import {
  type TInventoryTransfer,
  moduleServiceHandlers,
  serviceConfig,
  type TInventoryTransfers,
  catalogServiceHandlers,
} from '../../..'
import { defaultPagination, useNotification, useServiceHandlers } from '@/core'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { AxiosError } from 'axios'
import { TWarehouse } from '@/modules/warehouses'
import { TLocation } from '@/modules/locations'
import { toValue } from 'vue'

const { model } = serviceConfig

export const useModuleQuery = (options: BaseServiceOptions<Pagination>) => {
  const { currentPage, handlers, enableQuery, modelId, filters } = options

  const { errorHttpNotification } = useNotification()
  const serviceHandlers = useServiceHandlers<TInventoryTransfer>({
    model,
    currentPage: toValue(currentPage) ?? 1,
    filterParams: filters?.loadRecords ?? '',
  })

  const editRecord = useMutation<
    TInventoryTransfer,
    AxiosError,
    TInventoryTransfer
  >(
    (payload: TInventoryTransfer) =>
      moduleServiceHandlers.editRecord(payload, payload.id),
    {
      onSuccess: (payload) => serviceHandlers.updateRecord(payload),
      onError: (error) => errorHttpNotification(error),
    },
  )

  const addRecord = useMutation<
    TInventoryTransfer,
    AxiosError,
    TInventoryTransfer
  >((payload: TInventoryTransfer) => moduleServiceHandlers.addRecord(payload), {
    onSuccess: (payload) => serviceHandlers.addRecord(payload),
    onError: (error) => errorHttpNotification(error),
  })

  const actionRecord = useMutation<
    TInventoryTransfer,
    AxiosError,
    TRecordAction
  >((payload: TRecordAction) => moduleServiceHandlers.actionRecord(payload), {
    onSuccess: (payload) => serviceHandlers.updateRecord(payload),
    onError: (error) => errorHttpNotification(error),
  })
  const loadRecords = useQuery<TInventoryTransfers>(
    [model, currentPage, filters?.loadRecords],
    () =>
      moduleServiceHandlers.loadRecords({
        dynamicFilters: filters?.loadRecords,
        page: currentPage,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.loadRecords,
      onSuccess: (data) => {
        if (data.meta) {
          handlers?.onDataChange?.(data.meta ?? defaultPagination)
        }
      },
    },
  )

  const loadRecord = useQuery<TInventoryTransfer, AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) =>
      moduleServiceHandlers.loadRecord({
        id: Number(queryKey[1]),
      }),
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadRecord,
    onError: (error) => errorHttpNotification(error),
    select: (data) => ({
      ...data,
    }),
    onSuccess: (data) => {
      options.handlers?.onLoadRecord?.(data)
    },
  })

  const loadHistoryActions = useQuery<THistoryActions[], AxiosError>({
    queryKey: [model, modelId],
    queryFn: ({ queryKey }) => {
      return moduleServiceHandlers.loadHistoryActions({
        id: queryKey[1] as number,
      })
    },
    refetchOnWindowFocus: false,
    enabled: enableQuery?.loadHistoryActions,
    onError: (error) => errorHttpNotification(error),
  })

  const warehouseCatalog = useQuery<TWarehouse[]>(
    [serviceConfig.catalogs.warehouses, filters?.warehouseCatalog],
    () =>
      catalogServiceHandlers.warehouseCatalog.loadCatalog({
        dynamicFilters: filters?.warehouseCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.warehouseCatalog,
    },
  )

  const locationsCatalog = useQuery<TLocation[]>(
    [serviceConfig.catalogs.locations, filters?.locationsCatalog],
    () =>
      catalogServiceHandlers.locationsCatalog.loadCatalog({
        dynamicFilters: filters?.locationsCatalog,
      }),
    {
      refetchOnWindowFocus: false,
      enabled: enableQuery?.locationsCatalog,
    },
  )

  return {
    warehouseCatalog,
    locationsCatalog,
    loadHistoryActions,
    loadRecord,
    loadRecords,
    addRecord,
    editRecord,
    actionRecord,
  }
}
